// ============================================================================
//  DocumentDetailPage — Vue détaillée complète
//  Métadonnées · Commentaires · Historique versions · Renommer
// ============================================================================

import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeft, Download, Archive, FileText, File, Image,
  FileSpreadsheet, FilePieChart, MessageSquare, Clock,
  Tag, Edit2, Check, X, Trash2, RotateCcw, Plus, Eye,
} from 'lucide-react'
import { FilePreviewModal, downloadFromUrl } from '@/components/ui'
import {
  documentsApi,
  type Document,
  type DocumentComment,
  type DocumentVersion,
} from '@/modules/documents/api/documents.api'
import { useNotificationStore } from '@/stores/notification.store'
import { useAuthStore } from '@/stores/auth.store'

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmtSize = (b: number) =>
  b < 1024 ? `${b} B` : b < 1024 * 1024 ? `${(b / 1024).toFixed(1)} Ko` : `${(b / 1024 / 1024).toFixed(1)} Mo`

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric' })

const fmtDatetime = (d: string) =>
  new Date(d).toLocaleString('fr-SN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })

function DocIcon({ mimeType, className = 'w-8 h-8' }: { mimeType: string; className?: string }) {
  const map: [string, React.ElementType, string][] = [
    ['application/pdf',    FilePieChart,    'text-red-400'],
    ['image/',             Image,           'text-[var(--mauve)]'],
    ['application/vnd',    FileSpreadsheet, 'text-green-500'],
    ['text/',              FileText,        'text-[var(--blue-d)]'],
  ]
  const [, Icon, color] = map.find(([k]) => mimeType?.startsWith(k)) ?? [null, File, 'text-[var(--tx3)]']
  return <Icon className={`${className} ${color}`} />
}

function Avatar({ user, size = 'sm' }: { user?: { firstName?: string; lastName?: string; avatarUrl?: string }; size?: 'xs' | 'sm' }) {
  const s = size === 'xs' ? 'w-6 h-6 text-[10px]' : 'w-8 h-8 text-xs'
  const initials = `${user?.firstName?.[0] ?? '?'}${user?.lastName?.[0] ?? ''}`
  return (
    <div className={`${s} rounded-full bg-gradient-to-br from-primary-600 to-primary-500 flex items-center justify-center text-white font-bold shrink-0`}>
      {user?.avatarUrl
        ? <img src={user.avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
        : initials}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const addNotif = useNotificationStore(s => s.addNotification)
  const { user } = useAuthStore()

  const [activeTab, setActiveTab] = useState<'info' | 'comments' | 'versions'>('info')
  const [isRenaming, setIsRenaming] = useState(false)
  const [newName, setNewName] = useState('')
  const [commentText, setCommentText] = useState('')
  const [commentPage, setCommentPage] = useState('')
  const [previewOpen, setPreviewOpen] = useState(false)

  // ── Queries ─────────────────────────────────────────────────────────────────
  const { data: doc, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => documentsApi.getDocument(id!),
    enabled: !!id,
  })

  const { data: versions } = useQuery({
    queryKey: ['doc-versions', id],
    queryFn: () => documentsApi.getVersions(id!),
    enabled: !!id && activeTab === 'versions',
  })

  const { data: comments } = useQuery({
    queryKey: ['doc-comments', id],
    queryFn: () => documentsApi.getComments(id!),
    enabled: !!id && activeTab === 'comments',
  })

  // ── Mutations ────────────────────────────────────────────────────────────────
  const renameMutation = useMutation({
    mutationFn: () => documentsApi.update(id!, { name: newName.trim() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['document', id] })
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      setIsRenaming(false)
      addNotif({ id: Date.now().toString(), type: 'success', title: 'Document renommé' })
    },
    onError: (e: any) => addNotif({ id: Date.now().toString(), type: 'error', title: e.response?.data?.message ?? 'Erreur' }),
  })

  const archiveMutation = useMutation({
    mutationFn: () => documentsApi.archive(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      addNotif({ type: 'success', title: 'Document archivé' })
      navigate(-1)
    },
    onError: (e: any) => addNotif({ type: 'error', title: e.response?.data?.message ?? 'Archivage impossible' }),
  })

  const deleteMutation = useMutation({
    mutationFn: () => documentsApi.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['doc-folders'] })
      addNotif({ type: 'success', title: 'Document supprimé' })
      navigate(-1)
    },
    onError: (e: any) => addNotif({ type: 'error', title: e.response?.data?.message ?? 'Suppression impossible' }),
  })

  const addCommentMutation = useMutation({
    mutationFn: () => documentsApi.addComment(id!, {
      content: commentText.trim(),
      pageNumber: commentPage ? parseInt(commentPage) : undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-comments', id] })
      queryClient.invalidateQueries({ queryKey: ['document', id] })
      setCommentText('')
      setCommentPage('')
    },
    onError: (e: any) => addNotif({ id: Date.now().toString(), type: 'error', title: e.response?.data?.message ?? 'Erreur' }),
  })

  const resolveCommentMutation = useMutation({
    mutationFn: (commentId: string) => documentsApi.resolveComment(id!, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-comments', id] })
      queryClient.invalidateQueries({ queryKey: ['document', id] })
    },
    onError: (e: any) => addNotif({ type: 'error', title: e.response?.data?.message ?? 'Erreur' }),
  })

  const deleteCommentMutation = useMutation({
    mutationFn: (commentId: string) => documentsApi.deleteComment(id!, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doc-comments', id] })
      queryClient.invalidateQueries({ queryKey: ['document', id] })
    },
  })

  const downloadMutation = useMutation({
    mutationFn: () => documentsApi.getDownloadUrl(id!, 'attachment'),
    onSuccess: ({ url }) => downloadFromUrl(url),
    onError: () => addNotif({ id: Date.now().toString(), type: 'error', title: 'Erreur de téléchargement' }),
  })

  // ── Loading / error states ────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-[var(--blue)] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!doc) {
    return (
      <div className="p-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[var(--tx3)] hover:text-[var(--tx2)] mb-6">
          <ChevronLeft className="w-4 h-4" /> Retour
        </button>
        <p className="text-[var(--tx3)]">Document introuvable.</p>
      </div>
    )
  }

  // La liste dédiée (onglet) inclut les commentaires résolus ; la fiche (GET /:id) ne renvoie que les non résolus
  const docComments: DocumentComment[] = comments ?? doc.comments ?? []
  const isOwner = doc.uploadedBy?.id === user?.id
  const docVersions: DocumentVersion[] = versions ?? []
  const totalComments = doc._count?.comments ?? docComments.length

  const startRename = () => {
    setNewName(doc.name)
    setIsRenaming(true)
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--bd)] shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg text-[var(--tx3)] hover:text-[var(--tx)] transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <DocIcon mimeType={doc.mimeType} className="w-7 h-7" />
          <div>
            {isRenaming ? (
              <div className="flex items-center gap-2">
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newName.trim()) renameMutation.mutate()
                    if (e.key === 'Escape') setIsRenaming(false)
                  }}
                  autoFocus
                  className="px-2 py-1 bg-[var(--surface-2)] border border-[var(--blue)]/40 rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] min-w-[200px]"
                />
                <button
                  onClick={() => newName.trim() && renameMutation.mutate()}
                  disabled={!newName.trim() || renameMutation.isPending}
                  className="p-1.5 bg-[var(--blue)] text-white rounded-lg hover:opacity-90 disabled:opacity-40"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setIsRenaming(false)} className="p-1.5 hover:bg-[var(--surface-2)] rounded-lg text-[var(--tx3)]">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-base font-semibold text-[var(--tx)]">{doc.name}</h1>
                {isOwner && (
                  <button onClick={startRename} className="p-1 hover:bg-[var(--surface-2)] rounded text-[var(--tx4)] hover:text-[var(--tx3)] transition-colors" title="Renommer">
                    <Edit2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
            <p className="text-xs text-[var(--tx3)] mt-0.5">
              {doc.mimeType} · v{doc.version} · {fmtSize(doc.fileSize)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setPreviewOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[var(--bd)] hover:bg-[var(--surface-2)] text-[var(--tx)] rounded-lg text-sm font-medium transition-colors"
          >
            <Eye className="w-3.5 h-3.5" aria-hidden="true" />
            Aperçu
          </button>
          <button
            onClick={() => downloadMutation.mutate()}
            disabled={downloadMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" />
            Télécharger
          </button>
          {!doc.isArchived && isOwner && (
            <button
              onClick={() => window.confirm('Archiver ce document ?') && archiveMutation.mutate()}
              className="p-2 hover:bg-[var(--surface-2)] rounded-lg text-[var(--tx3)] hover:text-[var(--amber-text)] transition-colors"
              title="Archiver"
            >
              <Archive className="w-4 h-4" />
            </button>
          )}
          {isOwner && (
            <button
              onClick={() => window.confirm('Supprimer définitivement ce document (et ses versions antérieures) ?') && deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="p-2 hover:bg-red-50 rounded-lg text-[var(--tx3)] hover:text-red-500 transition-colors disabled:opacity-50"
              title="Supprimer définitivement"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Visionneuse (lecture seule) */}
      <FilePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        fileName={doc.name}
        mimeType={doc.mimeType}
        fileSize={doc.fileSize}
        getInlineUrl={() => documentsApi.getDownloadUrl(doc.id, 'inline')}
        getDownloadUrl={() => documentsApi.getDownloadUrl(doc.id, 'attachment').then(r => r.url)}
      />

      {/* Onglets */}
      <div className="flex border-b border-[var(--bd)] px-6 shrink-0">
        {([
          { key: 'info',     label: 'Informations' },
          { key: 'comments', label: `Commentaires (${totalComments})` },
          { key: 'versions', label: `Versions (v${doc.version})` },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-[var(--blue)] text-[var(--tx)]'
                : 'border-transparent text-[var(--tx3)] hover:text-[var(--tx)]'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6 space-y-5">

        {/* ── ONGLET INFO ─────────────────────────────────────────────── */}
        {activeTab === 'info' && (
          <>
            {/* Métadonnées */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Taille',    value: fmtSize(doc.fileSize) },
                { label: 'Version',   value: `v${doc.version}` },
                { label: 'Ajouté le', value: fmtDate(doc.createdAt) },
                { label: 'Modifié',   value: fmtDate(doc.updatedAt) },
              ].map(kpi => (
                <div key={kpi.label} className="bg-[var(--surface-2)] rounded-xl p-3">
                  <p className="text-[11px] text-[var(--tx3)] mb-1">{kpi.label}</p>
                  <p className="text-sm font-medium text-[var(--tx)]">{kpi.value}</p>
                </div>
              ))}
            </div>

            {/* Par */}
            <div className="flex items-center gap-3 p-4 bg-[var(--surface-2)] rounded-xl">
              <Avatar user={doc.uploadedBy} />
              <div>
                <p className="text-xs text-[var(--tx3)]">Uploadé par</p>
                <p className="text-sm font-medium text-[var(--tx)]">
                  {doc.uploadedBy?.firstName} {doc.uploadedBy?.lastName}
                </p>
              </div>
              {doc.folder && (
                <>
                  <div className="w-px h-8 bg-[var(--bd)] mx-2" />
                  <div>
                    <p className="text-xs text-[var(--tx3)]">Dossier</p>
                    <p className="text-sm font-medium text-[var(--tx)]">{doc.folder.name}</p>
                  </div>
                </>
              )}
            </div>

            {/* Description */}
            {doc.description && (
              <div className="p-4 bg-[var(--surface-2)] rounded-xl">
                <p className="text-xs text-[var(--tx3)] mb-2 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" /> Description
                </p>
                <p className="text-sm text-[var(--tx2)] leading-relaxed">{doc.description}</p>
              </div>
            )}

            {/* Tags */}
            {doc.tags && doc.tags.length > 0 && (
              <div>
                <p className="text-xs text-[var(--tx3)] mb-2 flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" /> Tags
                </p>
                <div className="flex flex-wrap gap-2">
                  {doc.tags.map((t: string) => (
                    <span key={t} className="px-2.5 py-1 bg-[var(--surface-2)] text-[var(--tx2)] text-xs rounded-full border border-[var(--bd)]">
                      #{t}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Statut archivé */}
            {doc.isArchived && (
              <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                <Archive className="w-4 h-4" /> Ce document est archivé
              </div>
            )}
          </>
        )}

        {/* ── ONGLET COMMENTAIRES ──────────────────────────────────────── */}
        {activeTab === 'comments' && (
          <>
            {/* Formulaire */}
            <div className="bg-[var(--surface-2)] rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-[var(--tx)]">Ajouter un commentaire</p>
              <textarea
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                placeholder="Votre commentaire…"
                rows={3}
                className="w-full px-3 py-2 bg-[var(--surface-1)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] placeholder:text-[var(--tx4)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)] resize-none"
              />
              <div className="flex items-center justify-between">
                <input
                  type="number"
                  min="1"
                  value={commentPage}
                  onChange={e => setCommentPage(e.target.value)}
                  placeholder="Page (optionnel)"
                  className="w-36 px-3 py-1.5 bg-[var(--surface-1)] border border-[var(--bd)] rounded-lg text-sm text-[var(--tx)] focus:outline-none focus:ring-1 focus:ring-[var(--blue)]"
                />
                <button
                  onClick={() => commentText.trim() && addCommentMutation.mutate()}
                  disabled={!commentText.trim() || addCommentMutation.isPending}
                  className="flex items-center gap-1.5 px-4 py-1.5 bg-[var(--blue)] hover:bg-[var(--blue-d)] text-white rounded-lg text-sm font-medium disabled:opacity-40 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {addCommentMutation.isPending ? 'Envoi…' : 'Commenter'}
                </button>
              </div>
            </div>

            {/* Liste */}
            {docComments.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-[var(--tx3)]">
                <MessageSquare className="w-8 h-8 mb-2 opacity-20" />
                <p className="text-sm">Aucun commentaire</p>
              </div>
            ) : (
              <div className="space-y-3">
                {docComments.map((c: DocumentComment) => (
                  <div key={c.id} className={`p-4 rounded-xl border ${c.resolvedAt ? 'bg-[var(--surface-2)] border-[var(--bd)] opacity-60' : 'bg-[var(--surface-1)] border-[var(--bd)]'}`}>
                    <div className="flex items-start gap-3">
                      <Avatar user={c.author} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-semibold text-[var(--tx)]">
                            {c.author?.firstName} {c.author?.lastName}
                          </span>
                          {c.pageNumber && (
                            <span className="text-[10px] text-[var(--tx4)] bg-[var(--surface-2)] px-1.5 py-0.5 rounded">
                              p. {c.pageNumber}
                            </span>
                          )}
                          {c.resolvedAt && (
                            <span className="text-[10px] text-green-600 flex items-center gap-0.5">
                              <Check className="w-3 h-3" /> Résolu
                            </span>
                          )}
                          <span className="text-[10px] text-[var(--tx4)] ml-auto">{fmtDatetime(c.createdAt)}</span>
                        </div>
                        <p className="text-sm text-[var(--tx2)] leading-relaxed">{c.content}</p>
                      </div>
                    </div>
                    {!c.resolvedAt && (
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--bd)]">
                        <button
                          onClick={() => resolveCommentMutation.mutate(c.id)}
                          className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700 transition-colors"
                        >
                          <Check className="w-3 h-3" /> Résoudre
                        </button>
                        {c.author?.id === user?.id && (
                          <button
                            onClick={() => window.confirm('Supprimer ce commentaire ?') && deleteCommentMutation.mutate(c.id)}
                            className="flex items-center gap-1 text-xs text-[var(--tx3)] hover:text-red-500 transition-colors ml-auto"
                          >
                            <Trash2 className="w-3 h-3" /> Supprimer
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── ONGLET VERSIONS ─────────────────────────────────────────── */}
        {activeTab === 'versions' && (
          <>
            {docVersions.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-[var(--tx3)]">
                <Clock className="w-8 h-8 mb-2 opacity-20" />
                <p className="text-sm">Chargement de l'historique…</p>
              </div>
            ) : (
              <div className="space-y-2">
                {docVersions.map((v: DocumentVersion) => (
                  <div
                    key={v.id}
                    className={`flex items-center gap-4 p-4 rounded-xl border transition-colors ${
                      v.isLatest
                        ? 'bg-[var(--blue)]/5 border-[var(--blue)]/30'
                        : 'bg-[var(--surface-2)] border-[var(--bd)]'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      v.isLatest ? 'bg-[var(--blue)] text-white' : 'bg-[var(--surface-3)] text-[var(--tx3)]'
                    }`}>
                      v{v.version}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--tx)] truncate">{v.fileName}</p>
                      <p className="text-xs text-[var(--tx3)]">{fmtSize(v.fileSize)} · {fmtDatetime(v.createdAt)}</p>
                    </div>
                    {v.isLatest && (
                      <span className="text-[10px] font-semibold text-[var(--blue)] bg-[var(--blue)]/10 px-2 py-0.5 rounded-full">
                        Actuelle
                      </span>
                    )}
                    {!v.isLatest && (
                      <button
                        onClick={() => window.open(`/documents/${v.id}`, '_blank')}
                        className="p-1.5 hover:bg-[var(--surface-3)] rounded-lg text-[var(--tx3)] hover:text-[var(--tx)] transition-colors"
                        title="Voir cette version"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
